import { Transition } from "@headlessui/react";
import { Checkmark, Icon } from "@shared/ui";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { ClientContext } from "../App";
import { useDestinationClientContext } from "../contexts/ClientContext";
import { useNavigate } from "../hooks/useNavigate";
import Search from "../icons/Search";
import { getClientId } from "../lib/automatic";
import { CLIENT_ID_PARAM, RESOURCE_ROUTE_PATTERN } from "../lib/constants";
import { handleInvalidClientIdError } from "../lib/errorHandler";
import LoadingSpinner from "./LoadingSpinner";

// eslint-disable-next-line
const ClientDropdown = ({ isOpen, onClose, onClientSelect }) => {
  const {
    platformClientConfig,
    selectedClient: contextClientId,
    setSelectedClient,
    platformClientId,
  } = useContext(ClientContext);

  const { clients, promiseInProgress } = useDestinationClientContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [isUpdatingClient, setIsUpdatingClient] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Use clientId from URL params if available, otherwise use the one from context
  const clientId = searchParams.get(CLIENT_ID_PARAM) || contextClientId;

  // Filter clients based on search query
  // eslint-disable-next-line
  const filteredClients = Array.isArray(clients)
    ? clients.filter((client) => {
        if (!client) return false;

        // Convert to string to avoid type errors
        const clientName = String(client.displayName || "");
        const clientId = String(client.id || "");
        const clientEntity = String(client.entity || "");
        const query = String(searchQuery).toLowerCase();

        return (
          clientName.toLowerCase().includes(query) ||
          clientId.toLowerCase().includes(query) ||
          clientEntity.toLowerCase().includes(query)
        );
      })
    : [];

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        // Safe way to check and call focus
        try {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        } catch (err) {
          // Silently ignore focus errors
        }
      }, 100);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        // Check if the click was outside the dropdown
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          onClose();
        }
      } catch (err) {
        // Fallback if contains method fails
        onClose();
      }
    };

    if (isOpen) {
      // Use mousedown instead of click to prevent issues with input focus
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevent dropdown from closing when clicking the search input
  useEffect(() => {
    const handleSearchInputClick = (event) => {
      event.stopPropagation();
    };

    if (searchInputRef.current) {
      searchInputRef.current.addEventListener(
        "mousedown",
        handleSearchInputClick
      );
    }

    return () => {
      if (searchInputRef.current) {
        searchInputRef.current.removeEventListener(
          "mousedown",
          handleSearchInputClick
        );
      }
    };
  }, []);

  const handleClientSelect = async (client) => {
    try {
      setIsUpdatingClient(true);

      // If on a resource detail page (e.g. /ignition/ign_123, /policies/pol_123,
      // /team/invite), the new client won't have access to that specific resource.
      // Navigate to the home page for this client instead
      const targetPath = RESOURCE_ROUTE_PATTERN.test(location.pathname)
        ? `/ignition`
        : location.pathname;

      const newParams = new URLSearchParams(searchParams);
      newParams.set(CLIENT_ID_PARAM, client.id);
      navigate(`${targetPath}?${newParams.toString()}`);
      onClientSelect(client);
    } catch (error) {
      console.error("Error selecting client:", error);
      // If there's an error, revert the URL to the previous client
      const [currentClient, getClientIdError] = await getClientId();
      if (getClientIdError) {
        handleInvalidClientIdError();
        return;
      }
      const newParams = new URLSearchParams(searchParams);
      newParams.set(CLIENT_ID_PARAM, currentClient);
      navigate(`/?${newParams.toString()}`);
    } finally {
      setIsUpdatingClient(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Transition
      show={isOpen}
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <div
        ref={dropdownRef}
        className="absolute top-11 left-0 w-60 bg-white rounded-md shadow-lg z-[9991] text-sm focus:outline-none"
      >
        {/* Search bar */}
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center bg-gray-50 rounded pl-3">
            <Icon
              icon={Search}
              height="h-4"
              width="w-4"
              className="text-gray-400"
            />
            <input
              ref={searchInputRef}
              type="text"
              className="bg-transparent text-sm grow p-3"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        {/* Content */}
        <div className="max-h-80 overflow-y-auto text-gray-400">
          {isUpdatingClient || promiseInProgress ? (
            <div className="p-4 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="py-2">
              {/* Platform Client */}
              <div className="pt-1 px-4 text-gray-500 font-medium text-xs">
                Platform Client
              </div>
              <div className="py-1">
                {platformClientConfig && platformClientId && (
                  <button
                    className={`w-full flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors hover:text-black ${
                      platformClientId === clientId
                        ? "text-black font-medium"
                        : ""
                    }`}
                    onClick={() => handleClientSelect(platformClientConfig)}
                    disabled={isUpdatingClient}
                  >
                    <div className="text-left flex-grow">
                      <div className="truncate">
                        {platformClientConfig.displayName || "My Account"}
                      </div>
                      {platformClientConfig.entity && (
                        <div
                          className={`text-xs leading-tight truncate font-normal ${
                            platformClientId === clientId
                              ? "text-black"
                              : "text-gray-400"
                          }`}
                        >
                          {platformClientConfig.entity}
                        </div>
                      )}
                    </div>
                    {platformClientId === clientId && (
                      <Icon
                        icon={Checkmark}
                        height="h-3"
                        width="w-3"
                        className="text-blue-500 flex-shrink-0"
                      />
                    )}
                  </button>
                )}
              </div>

              {/* Destination Clients */}
              {filteredClients.length > 0 && (
                <>
                  <div className="pt-3 px-4 text-gray-500 font-medium text-xs border-t border-gray-200">
                    Destination Clients
                  </div>
                  <div className="py-1">
                    {filteredClients.map((client) => (
                      <button
                        key={client.id}
                        className={`w-full flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors group hover:text-black ${
                          client.id === clientId ? "text-black font-medium" : ""
                        }`}
                        onClick={() => handleClientSelect(client)}
                        disabled={isUpdatingClient}
                      >
                        <div className="text-left flex-grow">
                          <div className="truncate">
                            {client.displayName || "Client"}
                          </div>
                          {client.entity && (
                            <div
                              className={`text-xs leading-tight truncate ${
                                client.id === clientId
                                  ? "text-black"
                                  : "text-gray-400 group-hover:text-black"
                              }`}
                            >
                              {client.entity}
                            </div>
                          )}
                        </div>
                        {client.id === clientId && (
                          <Icon
                            icon={Checkmark}
                            height="h-3"
                            width="w-3"
                            className="text-blue-500 flex-shrink-0"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </Transition>
  );
};

export default ClientDropdown;
